import { useMemo } from "react";

import type { CandidateStore } from "../../../../../../stores/automation/candidate.store";
import type { BranchRef } from "../../../../../../stores/automation/branches.store";
import type { ActionCandidate } from "../../../../../../types/candidate";

/**
 * The actions a Conditional Logic branch condition may read data from: the
 * actions on the block's own execution path, before the block. Sibling
 * branches never leak (upstreamActionsOf checkpoints/rolls back).
 *
 * BOTH sides of a branch condition share this scope. The right side used to be
 * derived from whichever action the left side resolved to, which left it with
 * no actions at all whenever the left field came from the trigger (BUG-21770).
 *
 * Memoized on a content fingerprint because `upstreamActionsOf` is a store
 * METHOD (not a computed) and returns a fresh array identity on every call --
 * without this, child `useEffect([actions])` hooks would re-run every render
 * (mirrors the useUpstreamActions fingerprint-memo).
 */
export const useConditionSourceActions = (
  candidate: CandidateStore,
  branchRef: BranchRef,
): ActionCandidate[] => {
  const base = candidate.branches.upstreamActionsOf(branchRef.instanceId);

  const fingerprint = base
    .map(
      (action) =>
        `${action.hash}:${action.actionReference?.instanceId ?? ""}:${
          action.actionName ?? ""
        }`,
    )
    .join("|");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fingerprint is the content key for `base`
  return useMemo(() => base, [fingerprint]);
};
